import { useState, useEffect } from "react";
import { ensureSupabaseAuthSession, useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "fastblog_master_admin";

interface CachedMasterAdmin {
  isMasterAdmin: boolean;
  userId: string;
}

function getCachedMasterAdmin(userId: string | undefined): CachedMasterAdmin | null {
  if (!userId) return null;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as CachedMasterAdmin;
      if (parsed.userId === userId) {
        return parsed;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedMasterAdmin(userId: string, isMasterAdmin: boolean) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ isMasterAdmin, userId }));
  } catch {
    // Ignore cache errors
  }
}

export function useMasterAdmin() {
  const { user, session, isLoading: authLoading } = useAuth();
  
  const cached = getCachedMasterAdmin(user?.id);
  const [isMasterAdmin, setIsMasterAdmin] = useState(cached?.isMasterAdmin ?? false);
  const [isLoading, setIsLoading] = useState(!cached);

  useEffect(() => {
    let cancelled = false;
    async function checkMasterAdmin() {
      if (authLoading) {
        setIsLoading(true);
        return;
      }

      if (!user) {
        if (cancelled) return;
        setIsMasterAdmin(false);
        setIsLoading(false);
        return;
      }

      const cachedData = getCachedMasterAdmin(user.id);
      if (cachedData) {
        setIsMasterAdmin(cachedData.isMasterAdmin);
      }

      try {
        const authReady = await ensureSupabaseAuthSession(session);
        if (!authReady) {
          if (cancelled) return;
          setIsMasterAdmin(cachedData?.isMasterAdmin ?? false);
          setIsLoading(cachedData ? false : true);
          return;
        }

        const { data, error } = await supabase.rpc("is_master_admin", { _user_id: user.id });

        if (error) {
          console.error("[useMasterAdmin] Error:", error);
          if (cancelled) return;
          setIsMasterAdmin(cachedData?.isMasterAdmin ?? false);
        } else {
          const isMaster = data === true;
          if (cancelled) return;
          setIsMasterAdmin(isMaster);
          setCachedMasterAdmin(user.id, isMaster);
        }
      } catch (err) {
        console.error("[useMasterAdmin] Exception:", err);
        if (cancelled) return;
        setIsMasterAdmin(cachedData?.isMasterAdmin ?? false);
      }
      
      if (!cancelled) setIsLoading(false);
    }

    checkMasterAdmin();
    return () => { cancelled = true; };
  }, [user, session, authLoading]);

  return { isMasterAdmin, isLoading };
}
